import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import GeneralLoading from '@/components/GeneralLoading';
import InputField from '@/components/InputField';
import { GO_DOCUMENTATION } from '@/constants/paths';

import useDevelopers from './useDevelopers';

interface WebhooksProps {
  id: string;
  value: string;
  handleCopy: (e: MouseEvent<HTMLButtonElement>, value: string) => void;
  handleDelete: (id: string) => void;
}

const Developers = () => {
  const theme = useTheme();
  const hook = useDevelopers();
  const { t } = useTranslation('developers');

  return (
    <Grid container spacing={2} direction="row">
      <Grid
        size={{ xxs: 12 }}
        mt={2}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column-reverse',
            gap: 1,
            justifyContent: 'center',
            alignItems: 'start',
          },
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
            gap: 1,
            justifyContent: 'center',
            alignItems: 'start',
          },
        }}
      >
        <Typography variant="subtitle2">{t('SUBTITLES.CREDENTIALS')}</Typography>
        <Button variant="outlined">
          <Link href={GO_DOCUMENTATION.developers} target="_blank" sx={{ textDecoration: 'none' }}>
            {t('BUTTONS.DOCUMENTATION')}
          </Link>
        </Button>
      </Grid>

      <Grid size={{ xxs: 12 }}>
        <InputField
          value={hook.credentials?.clientId}
          label={t('LABELS.CLIENT_ID')}
          name="developers.clientId"
          disabled
          sxInput={{ mt: 0 }}
          sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
          textFieldProps={{
            slotProps: {
              input: {
                endAdornment: (
                  <>
                    <IconButton
                      onClick={(e) => hook.handleCopy(e, hook.credentials?.clientId ?? '')}
                    >
                      {' '}
                      <ContentCopyIcon sx={{ cursor: 'pointer' }} />{' '}
                    </IconButton>
                  </>
                ),
              },
            },
          }}
        />
      </Grid>
      <Grid size={{ xxs: 12 }}>
        <InputField
          label={t('LABELS.CLIENTE_SECRET')}
          value={hook.credentials?.clientSecret}
          name="developers.ClientSecret"
          disabled
          sxInput={{ mt: 0 }}
          sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
          textFieldProps={{
            slotProps: {
              input: {
                endAdornment: (
                  <IconButton
                    onClick={(e) => hook.handleCopy(e, hook.credentials?.clientSecret ?? '')}
                  >
                    {' '}
                    <ContentCopyIcon sx={{ cursor: 'pointer' }} />{' '}
                  </IconButton>
                ),
              },
            },
          }}
        />
      </Grid>

      <Divider variant="fullWidth" sx={{ width: '100%', margin: '1rem auto 0' }} />

      <Typography
        sx={{
          marginBottom: '-1.4rem',
        }}
        variant="subtitle2"
      >
        {t('SUBTITLES.WEBHOOKS')}
      </Typography>

      <Grid
        size={{ xxs: 12 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          marginTop: '1rem',
        }}
      >
        {hook.webhooks.loading ? (
          <Box margin="1rem auto">
            <GeneralLoading size={50} text={t('LOADING')} fontVariant="h6" />
          </Box>
        ) : hook.webhooks.data.length === 0 ? (
          <Typography variant="body1">{t('ERROR.EMPTY')}</Typography>
        ) : (
          hook.webhooks.data.map((webhook) => (
            <Webhook
              key={webhook.url}
              id={webhook.id}
              value={webhook.url}
              handleCopy={hook.handleCopy}
              handleDelete={hook.handleDeleteWebhook}
            />
          ))
        )}
      </Grid>

      <Grid size={{ xxs: 12 }}>
        <Typography variant="body1">
          {t('NEW_REGISTER')} <br />
          <br />
          <a
            href={GO_DOCUMENTATION.webhooks}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              wordBreak: 'break-word',
            }}
            target="_blank"
          >
            {GO_DOCUMENTATION.webhooks}
          </a>
        </Typography>
      </Grid>
      <Grid size={{ xxs: 12 }}>
        <InputField
          value={hook.watch('url')}
          register={hook.register}
          label={t('LABELS.URL')}
          name="url"
          placeholder="https://my-service.com/webhook"
          sxInput={{ mt: 0 }}
          sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
          errors={hook.errors}
          disabled={hook.webhooks.data.length === 2}
        />
        {hook.webhooks.data.length === 2 && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.error.main,
              width: '100%',
              marginTop: '0.5rem',
            }}
          >
            {t('ERROR.MAX_WEBHOOKS')}
          </Typography>
        )}
      </Grid>
      <Grid
        size={{ xxs: 12 }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column',
            gap: 1,
            justifyContent: 'center',
          },
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: 1,
            justifyContent: 'center',
          },
        }}
      >
        <Button
          variant="contained"
          onClick={hook.handleSubmit((data) => hook.handleCreateWebhook(data.url))}
        >
          {t('BUTTONS.SAVE')}
        </Button>
      </Grid>
      <Popover
        id={hook.copyCode ? 'simple-popover' : undefined}
        open={Boolean(hook.copyCode)}
        anchorEl={hook.copyCode}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Typography p={1} variant="body2" color={theme.palette.secondary.main}>
          {t('COPY')}
        </Typography>
      </Popover>
    </Grid>
  );
};

export default Developers;

const Webhook = ({ id, value, handleCopy, handleDelete }: WebhooksProps) => {
  return (
    <Grid size={{ xxs: 12 }} sx={{ display: 'flex', alignItems: 'end', gap: 2 }}>
      <InputField
        value={value}
        name={value}
        disabled
        textFieldProps={{
          slotProps: {
            input: {
              endAdornment: (
                <IconButton onClick={(e) => handleCopy(e, value ?? '')}>
                  <ContentCopyIcon sx={{ cursor: 'pointer' }} />
                </IconButton>
              ),
            },
          },
        }}
      />
      <IconButton onClick={() => handleDelete(id)}>
        <DeleteForeverIcon sx={{ cursor: 'pointer' }} fontSize="large" />
      </IconButton>
    </Grid>
  );
};
